/**
 * Site logo (optionally linking to the site): `<SiteLogo>`.
 *
 * Renders a site logo. The logo is sourced from deployment config, supports
 * mobile and desktop sources, and becomes a link when `logoLinkUrl` is set.
 * Multi-language silos can configure per-language logos via `logoByLanguage`,
 * resolved from the hostname (`logoLanguageByHostname`, for silos served from
 * one domain per language) and falling back to the current UI language.
 */
import { useSiloLanguage } from 'component/translation';
import { Image, Pressable } from 'react-native';
import { useDatastore, useSiloKey } from 'util/datastore';
import { getDeploymentConfig } from 'util/util';

import { AccessiblePointerTarget } from './basics';

type SiteLogoProps = {
    large?: boolean | undefined;
};

export function SiteLogo({ large }: SiteLogoProps) {
    const datastore = useDatastore();
    const deploymentConfig = getDeploymentConfig();
    const siloKey = useSiloKey();
    const language = useSiloLanguage();

    const header = deploymentConfig.siloConfig?.[siloKey]?.standalonePageConfig?.header || {};
    const { logo, alt, logoLinkUrl, logoByLanguage, logoLanguageByHostname } = header;

    // Resolve the per-language logo: hostname mapping first (silos served
    // from one domain per language), then the current UI language
    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
    const logoLanguage = logoLanguageByHostname?.[hostname] ?? language.split('-')[0] ?? '';
    const languageLogo = logoByLanguage?.[logoLanguage];

    const logoSource = languageLogo?.logo ?? (large ? logo?.desktop : logo?.mobile);
    if (!logoSource) {
        return null;
    }

    // determine if source is a URL string or a local require() asset
    const imageSource = typeof logoSource === 'string' ? { uri: logoSource } : logoSource;

    // alt text for accessibility
    const altText = languageLogo?.alt ?? alt ?? deploymentConfig.psiBrandName ?? 'Logo';

    const handleLogoPress = () => {
        if (logoLinkUrl) datastore.openUrl(logoLinkUrl, '_self');
    };

    const renderedLogo = <Image accessibilityLabel={altText} resizeMode='contain' source={imageSource} />;

    // If no link configured, don't make it clickable
    if (!logoLinkUrl) {
        return renderedLogo;
    }

    return (
        <Pressable accessibilityLabel={altText} accessibilityRole='link' onPress={handleLogoPress}>
            <AccessiblePointerTarget>{renderedLogo}</AccessiblePointerTarget>
        </Pressable>
    );
}
